let isExist = false;

interface ShowLoginOptions {
  onClose?: () => void;
  onSuccess?: () => void;
}

export function isAuthDisabled() {
  return import.meta.env.DEV || import.meta.env.VITE_DISABLE_AUTH === 'true';
}

export function showLogin(options: ShowLoginOptions = {}) {
  if (isAuthDisabled() || isExist) {
    return;
  }
  isExist = true;
  loadResources().then(() => {
    if (window.SignPanel) {
      let closed = false;
      const handleClose = () => {
        if (closed) return;
        closed = true;
        isExist = false;
        options.onClose?.();
      };
      const stopWatchingClose = watchSignPanelClose(handleClose);

      new window.SignPanel({
        signNum: '0',
        callback: () => {
          closed = true;
          stopWatchingClose();
          if (options.onSuccess) {
            options.onSuccess();
            return;
          }

          window.location.reload();
        },
        closeCallback: () => {
          stopWatchingClose();
          handleClose();
        }
      });
    }
  });
}

function watchSignPanelClose(onClose: () => void) {
  const beforeElements = new Set(Array.from(document.body.querySelectorAll('*')));
  let panelElements: HTMLElement[] = [];
  let stopped = false;
  let hasVisiblePanel = false;

  const isVisible = (element: HTMLElement) => {
    const style = window.getComputedStyle(element);
    return (
      element.isConnected &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity) !== 0 &&
      element.getClientRects().length > 0
    );
  };

  const collectPanelElements = () => {
    panelElements = Array.from(document.body.querySelectorAll('*')).filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement && !beforeElements.has(element)
    );
  };

  const checkClosed = () => {
    if (stopped) return;
    if (panelElements.length === 0) {
      collectPanelElements();
      return;
    }

    hasVisiblePanel ||= panelElements.some(isVisible);
    if (!hasVisiblePanel) return;

    if (panelElements.every((element) => !isVisible(element))) {
      stop();
      onClose();
    }
  };

  const observer = new MutationObserver(checkClosed);
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
    childList: true,
    subtree: true
  });

  const collectTimer = window.setTimeout(() => {
    collectPanelElements();
    checkClosed();
  }, 0);
  const interval = window.setInterval(checkClosed, 200);

  function stop() {
    if (stopped) return;
    stopped = true;
    window.clearTimeout(collectTimer);
    window.clearInterval(interval);
    observer.disconnect();
  }

  return stop;
}

function loadResources() {
  return new Promise<void>((resolve, reject) => {
    const ts = Date.now();
    const isLocalhost = location.hostname.match('localhost') || location.hostname.match('192.168');
    const base = isLocalhost ? 'https://test-lifang.yunzhan365.com' : '';

    const existingScript = Array.from(document.querySelectorAll('script')).find(
      (script) => script.src && script.src.includes('/libs/login/login.min.js')
    );
    if (existingScript) {
      resolve();
      return;
    }

    const link = document.createElement('link');
    link.type = 'text/css';
    link.rel = 'stylesheet';
    link.href = base + '/libs/login/login.min.css?v=' + ts;
    document.head.appendChild(link);

    const jqueryScript = document.createElement('script');
    jqueryScript.type = 'text/javascript';
    jqueryScript.src = base + '/libs/jquery-3.5.1.min.js?v=' + ts;
    jqueryScript.onload = () => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = base + '/libs/login/login.min.js?v=' + ts;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load login.min.js'));
      document.head.appendChild(script);
    };
    jqueryScript.onerror = () => reject(new Error('Failed to load jquery'));
    document.head.appendChild(jqueryScript);
  });
}
